import { t, type TranslationKeys } from '@signage-player/renderer-ui';
import { AuthInstance } from '@signage-player/data-store';
import { getKnownInstances, isObserverInstance, peekUrlDisplayId, peekUrlInstanceId } from '@signage-player/device-browser';
import { navigateToPicker } from './instanceHistory';
import {
  autoFullscreenAllowed,
  enterFullscreen,
  fullscreenOnStart,
  fullscreenSupported,
  isFullscreen,
  setFullscreenOnStart,
  toggleFullscreen,
} from './webFullscreen';
import { installState, isIOS, isStandalone, onInstallChange, promptInstall, soundPermanentKey, wasInstalledHere } from './webInstall';
import { effectiveViewport, getRotation } from './webRotation';
import { installSoundProbe, mutedPlayback, soundStatus, unlockSound, videoSoundEnabled } from './webSound';

export interface WebSettingsOptions {
  /** Current auth instance; null before the container exists or on failure. */
  getAuthInstance: () => Promise<AuthInstance | null>;
}

const DASHBOARD_URL = 'http://app.bsgn.cc/';

const GEAR_SVG = `<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>`;
const CLOSE_SVG = `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>`;

const ua = navigator.userAgent;

let modal: HTMLElement | null = null;
let gear: HTMLButtonElement | null = null;
const modalCleanup: Array<() => void> = [];

/**
 * Web-only settings chrome: a gear button (top-right) that rides the
 * webChrome activity class — shown while the pointer moves / after a tap,
 * gone after the idle timeout — and opens a modal with player info,
 * fullscreen and the browser's autoplay-with-sound state.
 */
export function installWebSettings(options: WebSettingsOptions): void {
  injectStyles();
  installSoundProbe();

  // Cross-origin iframes (WEB playables) swallow pointer events, so the
  // corner keeps a parent-document target the activity listener can see.
  const hotspot = document.createElement('div');
  hotspot.className = 'web-settings-hotspot';
  hotspot.setAttribute('aria-hidden', 'true');

  gear = document.createElement('button');
  gear.type = 'button';
  gear.className = 'web-settings-gear';
  gear.innerHTML = GEAR_SVG;
  gear.title = t('settings_open');
  gear.setAttribute('aria-label', t('settings_open'));
  gear.setAttribute('aria-haspopup', 'dialog');
  gear.setAttribute('aria-expanded', 'false');
  gear.addEventListener('click', () => (modal ? closeModal() : openModal(options)));
  document.body.append(hotspot, gear);

  window.addEventListener(
    'keydown',
    e => {
      if (!modal || e.key !== 'Escape') return;
      // Capture phase: renderer-ui Keybinds maps a document-level Escape to quitApplication.
      e.stopPropagation();
      e.preventDefault();
      closeModal();
    },
    true
  );
}

// ---- info ------------------------------------------------------------------

function hostOf(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
}

function browserLabel(): string {
  const nav = navigator as Navigator & { userAgentData?: { brands: { brand: string; version: string }[]; platform: string } };
  const brands = nav.userAgentData?.brands?.filter(b => !/Not.?A.?Brand/i.test(b.brand)) ?? [];
  const primary = brands.find(b => !/Chromium/i.test(b.brand)) ?? brands[0];
  let name = primary ? `${primary.brand} ${primary.version}` : '';
  if (!name) {
    const m = ua.match(/(Firefox|FxiOS|CriOS|EdgiOS|Edg|OPR|Chrome|Version)\/(\d+)/);
    const names: Record<string, string> = { FxiOS: 'Firefox', CriOS: 'Chrome', EdgiOS: 'Edge', Edg: 'Edge', OPR: 'Opera', Version: 'Safari' };
    if (m) name = `${names[m[1]] ?? m[1]} ${m[2]}`;
  }
  const platform =
    (isIOS && 'iOS') ||
    nav.userAgentData?.platform ||
    (/Android/.test(ua) && 'Android') ||
    (/CrOS/.test(ua) && 'ChromeOS') ||
    (/Windows/.test(ua) && 'Windows') ||
    (/Macintosh/.test(ua) && 'macOS') ||
    (/Linux/.test(ua) && 'Linux') ||
    '';
  return [name, platform].filter(Boolean).join(' · ') || ua;
}

// ---- modal -----------------------------------------------------------------

function h<K extends keyof HTMLElementTagNameMap>(tag: K, className?: string, text?: string): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  if (className) el.className = className;
  if (text !== undefined) el.textContent = text;
  return el;
}

function button(className: string, text?: string): HTMLButtonElement {
  const b = h('button', className, text);
  b.type = 'button';
  return b;
}

function section(titleKey: TranslationKeys): HTMLElement {
  const s = h('section', 'ws-section');
  s.appendChild(h('h3', undefined, t(titleKey)));
  return s;
}

function infoRow(dl: HTMLElement, label: string): HTMLElement {
  dl.appendChild(h('dt', undefined, label));
  const dd = h('dd');
  dl.appendChild(dd);
  return dd;
}

function openModal(options: WebSettingsOptions): void {
  if (modal) return;
  const overlay = h('div', 'web-settings');
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-labelledby', 'web-settings-title');
  const backdrop = h('div', 'web-settings-backdrop');
  backdrop.addEventListener('click', closeModal);

  const panel = h('div', 'web-settings-panel');
  const head = h('div', 'ws-head');
  const title = h('h2', undefined, t('settings_title'));
  title.id = 'web-settings-title';
  const close = button('ws-close');
  close.innerHTML = CLOSE_SVG;
  close.title = t('settings_close');
  close.setAttribute('aria-label', t('settings_close'));
  close.addEventListener('click', closeModal);
  head.append(title, close);

  panel.append(head, buildInfo(options), buildScreen(), buildApp(), buildSound(), buildActions());
  overlay.append(backdrop, panel);
  document.body.appendChild(overlay);
  modal = overlay;
  document.body.classList.add('settings-open');
  gear?.setAttribute('aria-expanded', 'true');
  close.focus();
}

function closeModal(): void {
  if (!modal) return;
  for (const fn of modalCleanup.splice(0)) fn();
  modal.remove();
  modal = null;
  document.body.classList.remove('settings-open');
  gear?.setAttribute('aria-expanded', 'false');
  gear?.focus({ preventScroll: true });
}

function buildInfo(options: WebSettingsOptions): HTMLElement {
  const s = section('settings_section_info');
  const dl = h('dl', 'ws-info');
  infoRow(dl, t('settings_version')).textContent = __APP_VERSION__;

  // Peek only — resolveInstanceId() would mint an id if the URL had none.
  const id = peekUrlInstanceId();
  const inst = id ? (getKnownInstances().find(k => k.id === id) ?? null) : null;
  const displayId = peekUrlDisplayId();
  const display = inst?.displays.find(d => d.id === displayId) ?? null;
  const player = infoRow(dl, t('settings_instance'));
  // Names come from the backend, notes from user input — textContent only.
  player.appendChild(h('span', undefined, inst?.note || inst?.name || (id ? t('picker_unnamed') : '—')));
  if (id && isObserverInstance(id)) player.appendChild(h('span', 'ws-badge', t('observer_badge')));
  if (id) player.appendChild(h('div', 'ws-mono', id));
  if (displayId !== null) {
    infoRow(dl, t('settings_display')).textContent = display?.name ? `${display.name} [${displayId}]` : `[${displayId}]`;
  }

  const status = infoRow(dl, t('settings_status'));
  const server = infoRow(dl, t('settings_server'));
  status.textContent = '…';
  server.textContent = '…';
  void options.getAuthInstance().then(auth => {
    status.textContent = auth ? t(auth.isPaired ? 'settings_paired' : 'settings_unpaired') : '—';
    server.textContent = hostOf(auth?.url) ?? hostOf(import.meta.env.VITE_AUTH_SERVER_URL) ?? '—';
  });

  infoRow(dl, t('settings_viewport')).textContent = viewportLabel();
  infoRow(dl, t('settings_browser')).textContent = browserLabel();
  s.appendChild(dl);
  return s;
}

// Rotation comes from the dashboard's display orientation (webRotation.ts).
function viewportLabel(): string {
  const rotation = getRotation();
  const dpr = Math.round((window.devicePixelRatio || 1) * 100) / 100;
  let text = `${window.innerWidth}×${window.innerHeight} @${dpr}x`;
  if (rotation !== 0) {
    const { width, height } = effectiveViewport(rotation);
    text += ` · ${rotation}° → ${width}×${height}`;
  }
  return text;
}

function buildScreen(): HTMLElement {
  const s = section('settings_section_screen');
  const fsRow = h('div', 'ws-row');
  fsRow.appendChild(h('span', 'ws-label', t('settings_fullscreen')));
  if (fullscreenSupported()) {
    const btn = button('ws-btn');
    const label = () => {
      btn.textContent = t(isFullscreen() ? 'settings_fullscreen_exit' : 'settings_fullscreen_enter');
    };
    label();
    btn.addEventListener('click', () => void toggleFullscreen());
    document.addEventListener('fullscreenchange', label);
    document.addEventListener('webkitfullscreenchange', label);
    modalCleanup.push(() => {
      document.removeEventListener('fullscreenchange', label);
      document.removeEventListener('webkitfullscreenchange', label);
    });
    fsRow.appendChild(btn);
    s.appendChild(fsRow);

    // A <label> row so the text toggles the checkbox and names it.
    const startRow = h('label', 'ws-row');
    startRow.appendChild(h('span', 'ws-label', t('settings_fullscreen_on_start')));
    const check = h('input', 'ws-check');
    check.type = 'checkbox';
    check.checked = fullscreenOnStart();
    startRow.appendChild(check);
    s.appendChild(startRow);
    const hint = h('p', 'ws-hint');
    s.appendChild(hint);
    const updateHint = () => {
      void autoFullscreenAllowed().then(allowed => {
        if (!modal) return;
        hint.textContent =
          allowed && check.checked
            ? t('settings_fullscreen_auto_policy')
            : `${t('settings_fullscreen_on_start_hint')} ${t('settings_fullscreen_auto_policy_hint')}`;
      });
    };
    check.addEventListener('change', () => {
      setFullscreenOnStart(check.checked);
      // Inside the click's activation: the browser accepts the request from here.
      if (check.checked && !isFullscreen()) void enterFullscreen();
      updateHint();
    });
    updateHint();
  } else {
    const standalone = isStandalone();
    fsRow.appendChild(h('span', 'ws-status', t(standalone ? 'settings_fullscreen_standalone' : 'settings_fullscreen_unavailable')));
    s.appendChild(fsRow);
    if (isIOS && !standalone) s.appendChild(h('p', 'ws-hint', t('settings_fullscreen_ios_hint')));
  }
  return s;
}

function buildApp(): HTMLElement {
  const s = section('settings_section_app');
  const row = h('div', 'ws-row');
  row.appendChild(h('span', 'ws-label', t('settings_app_mode')));
  const status = h('span', 'ws-status');
  const install = button('ws-btn hidden', t('settings_app_install'));
  row.append(status, install);
  s.appendChild(row);
  const hint = h('p', 'ws-hint');
  s.appendChild(hint);

  const refresh = () => {
    const state = installState();
    const key =
      state === 'standalone' ? 'settings_app_standalone' : state === 'installed-in-tab' ? 'settings_app_installed_in_tab' : 'settings_app_browser_tab';
    status.textContent = t(key);
    status.classList.toggle('ok', state === 'standalone');
    install.classList.toggle('hidden', state !== 'promptable');
    hint.textContent = wasInstalledHere()
      ? t('issue_installed_fix')
      : state === 'standalone' && !isIOS
        ? t('settings_app_hint_standalone')
        : t(soundPermanentKey());
  };
  // promptInstall() first: prompt() consumes the click's user activation.
  install.addEventListener('click', () => void promptInstall().then(() => modal && refresh()));
  modalCleanup.push(onInstallChange(refresh));
  refresh();
  return s;
}

function buildSound(): HTMLElement {
  const s = section('settings_section_sound');
  const row = h('div', 'ws-row');
  row.appendChild(h('span', 'ws-label', t('settings_sound_autoplay')));
  const status = h('span', 'ws-status', t('settings_sound_checking'));
  const enable = button('ws-btn hidden', t('settings_sound_enable'));
  row.append(status, enable);
  s.appendChild(row);
  s.appendChild(h('p', 'ws-hint', t('settings_sound_hint')));
  if (!videoSoundEnabled) s.appendChild(h('p', 'ws-hint', t('settings_sound_video_config_muted')));

  let seq = 0;
  const refresh = async () => {
    const my = ++seq;
    const st = await soundStatus();
    if (my !== seq || !modal) return;
    status.textContent = t(st === 'allowed' ? 'settings_sound_allowed' : st === 'blocked' ? 'settings_sound_blocked' : 'settings_sound_unknown');
    status.classList.toggle('ok', st === 'allowed');
    status.classList.toggle('bad', st === 'blocked');
    enable.classList.toggle('hidden', st === 'allowed' && !mutedPlayback());
  };
  enable.addEventListener('click', () => {
    unlockSound();
    void refresh();
  });
  void refresh();
  return s;
}

function buildActions(): HTMLElement {
  const row = h('div', 'ws-actions');
  const switchPlayer = button('ws-btn', t('settings_switch_player'));
  switchPlayer.addEventListener('click', () => {
    closeModal();
    navigateToPicker();
  });
  const reload = button('ws-btn', t('settings_reload'));
  reload.addEventListener('click', () => window.location.reload());
  const dashboard = h('a', 'ws-btn ws-link', t('dashboard_link'));
  dashboard.href = DASHBOARD_URL;
  dashboard.target = '_blank';
  dashboard.rel = 'noopener';
  row.append(switchPlayer, reload, dashboard);
  return row;
}

// ---- styles ----------------------------------------------------------------

function injectStyles(): void {
  const style = document.createElement('style');
  style.textContent = `
    .web-settings-hotspot { position: fixed; top: 0; right: 0; width: 96px; height: 96px; z-index: 8999; }
    .web-settings-gear { position: fixed; top: 35px; right: 43px; z-index: 9000; display: flex; align-items: center;
      justify-content: center; width: 40px; height: 40px; padding: 0; border: 1px solid #2c3a4d; border-radius: 8px;
      background: #0d1626; color: #e8e8e8; opacity: 0; pointer-events: none;
      transition: opacity 0.2s ease, border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
    body.cursor-active .web-settings-gear, body.settings-open .web-settings-gear { opacity: 1; pointer-events: auto; }
    .web-settings-gear:hover, body.settings-open .web-settings-gear { border-color: #3989eb; background: #14203a; color: #5da7ff; }
    .web-settings-gear:focus-visible { outline: 2px solid #5da7ff; outline-offset: 2px; }
    @media screen and (max-width: 950px) { .web-settings-gear { top: 16px; right: 16px; } }

    .web-settings { position: fixed; inset: 0; z-index: 9500; display: flex; align-items: center; justify-content: center;
      padding: 16px; box-sizing: border-box; font-family: 'Poppins', system-ui, sans-serif; color: #e8e8e8; }
    .web-settings, .web-settings * { cursor: default; }
    .web-settings button, .web-settings button *, .web-settings a, .web-settings a * { cursor: pointer; }
    .web-settings-backdrop { position: absolute; inset: 0; background: rgba(8, 13, 22, 0.72); }
    .web-settings-panel { position: relative; width: 560px; max-width: 100%; max-height: 100%; overflow-y: auto;
      box-sizing: border-box; padding: 22px 26px 24px; border: 1px solid #2c3a4d; border-radius: 12px; background: #0d1626;
      box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5); }
    .ws-head { display: flex; justify-content: space-between; align-items: center; gap: 16px; margin-bottom: 10px; }
    .ws-head h2 { margin: 0; font-size: 22px; font-weight: 600; }
    .ws-close { display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; padding: 0;
      border: 1px solid #2c3a4d; border-radius: 8px; background: #0d1626; color: #e8e8e8;
      transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
    .ws-close:hover { border-color: #3989eb; background: #14203a; color: #5da7ff; }
    .ws-section { padding: 14px 0; border-top: 1px solid #2c3a4d; }
    .ws-section h3 { margin: 0 0 10px; font-size: 12px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase;
      color: #afafaf; }
    .ws-info { display: grid; grid-template-columns: max-content 1fr; gap: 6px 18px; margin: 0; font-size: 14px; }
    .ws-info dt { color: #afafaf; }
    .ws-info dd { margin: 0; min-width: 0; overflow-wrap: anywhere; }
    .ws-mono { font-size: 12px; color: #afafaf; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; }
    .ws-badge { display: inline-block; margin-left: 8px; padding: 1px 8px; border-radius: 10px; font-size: 12px;
      background: rgba(255, 255, 255, 0.12); color: #cfd2d6; vertical-align: middle; }
    .ws-row { display: flex; align-items: center; justify-content: space-between; gap: 12px 16px; flex-wrap: wrap;
      padding: 6px 0; font-size: 15px; }
    .ws-row .ws-label { flex: 1 1 160px; }
    .ws-btn { display: inline-block; padding: 8px 16px; border: 1px solid #2c3a4d; border-radius: 8px; background: #0d1626;
      color: #e8e8e8; font: inherit; font-size: 14px; line-height: 1.3; text-decoration: none; white-space: nowrap;
      transition: border-color 0.12s ease, background 0.12s ease, color 0.12s ease; }
    .ws-btn:hover { border-color: #3989eb; background: #14203a; color: #5da7ff; }
    .ws-btn.hidden { display: none; }
    .ws-check { flex: none; width: 18px; height: 18px; margin: 0; accent-color: #3989eb; cursor: pointer; }
    .ws-link::after { content: ' ↗'; color: #afafaf; }
    .ws-link:hover::after { color: #5da7ff; }
    .ws-status { color: #afafaf; font-size: 14px; }
    .ws-status.ok { color: #6ac258; }
    .ws-status.bad { color: #ffb454; }
    .ws-hint { margin: 4px 0 0; font-size: 13px; line-height: 1.45; color: #afafaf; }
    .ws-actions { display: flex; flex-wrap: wrap; gap: 10px; padding-top: 16px; border-top: 1px solid #2c3a4d; }
    @media screen and (max-width: 640px) {
      .web-settings-panel { padding: 18px 16px 20px; }
      .ws-info { grid-template-columns: 1fr; gap: 2px 0; }
      .ws-info dt { margin-top: 8px; }
      .ws-actions .ws-btn { flex: 1; text-align: center; }
    }
    @media (prefers-color-scheme: light) {
      .web-settings-gear { color: #181818; border-color: #c7c9cf; background: #ffffff; }
      .web-settings-gear:hover, body.settings-open .web-settings-gear { color: #1f75de; border-color: #1f75de; background: #ffffff; }
      .web-settings { color: #181818; }
      .web-settings-backdrop { background: rgba(0, 0, 0, 0.35); }
      .web-settings-panel { background: #ffffff; border-color: #c7c9cf; box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25); }
      .ws-close, .ws-btn { color: #181818; border-color: #c7c9cf; background: #ffffff; }
      .ws-close:hover, .ws-btn:hover { color: #1f75de; border-color: #1f75de; background: #ffffff; }
      .ws-section, .ws-actions { border-color: #e3e5e9; }
      .ws-section h3, .ws-info dt, .ws-mono, .ws-status, .ws-hint, .ws-link::after { color: #5a5d63; }
      .ws-link:hover::after { color: #1f75de; }
      .ws-badge { background: rgba(0, 0, 0, 0.08); color: #5a5d63; }
      .ws-status.ok { color: #2f8f3a; }
      .ws-status.bad { color: #c4700a; }
    }
  `;
  document.head.appendChild(style);
}
